import { format, isValid, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { PRIORITY_MAPPING } from './constants'

/**
 * Generate a random task ID
 * @returns {string} Task ID in the format TSK-XXXXX
 */
export const generateTaskId = () => {
  return 'TSK-' + Math.random().toString(36).substr(2, 9).toUpperCase()
}

/**
 * Format a date to YYYY-MM-DD format
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
export const formatDate = date => {
  return date.toISOString().split('T')[0]
}

/**
 * Get current date in YYYY-MM-DD format
 * @returns {string} Today's date as string
 */
export const getCurrentDate = () => {
  return formatDate(new Date())
}

/**
 * Map UI priority value to database value
 * @param {string} uiPriority - Priority as shown in UI
 * @returns {string} Priority value for database
 */
export const mapPriorityToDb = uiPriority => {
  return PRIORITY_MAPPING[uiPriority] || 'moyen'
}

/**
 * Determine the location_type based on the selected location
 * @param {Object} selectedSlope - The selected location object
 * @returns {string} The location type ('slope', 'lift', or 'custom')
 */
const determineLocationType = selectedSlope => {
  // Si location_type est explicitement défini (ex: 'lift' depuis LocationSelector)
  if (selectedSlope.location_type) {
    // Si c'est 'lift', on garde 'lift'
    if (selectedSlope.location_type === 'lift') {
      return 'lift'
    }
    // Pour les autres types custom (area, building, other), on garde 'custom'
    return 'custom'
  }
  // Par défaut, c'est une piste
  return 'slope'
}

/**
 * Create task data object ready for API submission
 * @param {Object} taskInfo - Task information from form
 * @returns {Object} Formatted task data
 */
export const createTaskDataForSubmission = ({
  selectedService,
  selectedSlope,
  executionDate,
  description,
  markerPosition,
  priority,
  photos,
  userId,
  resortId,
}) => {
  if (!selectedService || !selectedSlope || !executionDate || !markerPosition) {
    throw new Error('Missing required task information')
  }

  // MODIFIÉ: Utilise la nouvelle fonction pour déterminer le location_type
  const locationType = determineLocationType(selectedSlope)

  const taskData = {
    typeService: selectedService,
    piste: selectedSlope.nom || selectedSlope.name,
    dateExecution: new Date(executionDate).toISOString(),
    commentaire: description || '',
    long: parseFloat(markerPosition.lng),
    lat: parseFloat(markerPosition.lat),
    resort_id: parseInt(resortId),
    priority: mapPriorityToDb(priority),
    requestor_id: parseInt(userId),
    photos: photos,
    location_type: locationType,
    location_id: selectedSlope.id ? parseInt(selectedSlope.id) : null,
    // Ajouter le secteur si disponible
    sector: selectedSlope.sector || null,
  }

  return taskData
}

/**
 * Get location type label for display
 * @param {string} locationType - Location type code
 * @returns {string} User-friendly location type label
 */
export const getLocationTypeLabel = locationType => {
  if (!locationType) return 'Piste'

  switch (locationType) {
    case 'slope':
      return 'Piste'
    case 'lift':
      return 'Remontée'
    case 'area':
      return 'Zone'
    case 'building':
      return 'Bâtiment'
    case 'custom':
      return 'Personnalisé'
    default:
      return 'Autre'
  }
}

/**
 * Robustly parse a task date (ISO first, DD/MM/YYYY fallback, timezone-safe).
 * @param {string|Date} dateStr - Date string or Date object
 * @returns {Date|null} Parsed date, or null if invalid
 */
const parseTaskDate = dateStr => {
  if (!dateStr) return null
  let date
  if (typeof dateStr === 'string') {
    date = parseISO(dateStr)
    if (!isValid(date)) {
      const parts = dateStr.split('/')
      if (parts.length === 3) {
        const [day, month, year] = parts
        date = new Date(`${year}-${month}-${day}T00:00:00`)
      }
    }
  } else {
    date = dateStr
  }
  return isValid(date) ? date : null
}

/**
 * Format a date as a long French label, e.g. "Mardi 23 juin 2026".
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
const formatLongFr = date => {
  const dayName = format(date, 'EEEE', { locale: fr })
  return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${format(date, 'd')} ${format(date, 'MMMM', { locale: fr })} ${format(date, 'yyyy')}`
}

/**
 * Date de réalisation : le jour saisi (la nuit où la tâche est groomée). Pas de décalage.
 * @param {string} dateStr - Date string (ISO or DD/MM/YYYY)
 * @returns {string} Formatted realization date
 */
export const formatRealizationDate = dateStr => {
  const date = parseTaskDate(dateStr)
  return date ? formatLongFr(date) : dateStr || ''
}

/**
 * Date de disponibilité/livraison : le lendemain matin (J+1).
 * @param {string} dateStr - Date string (ISO or DD/MM/YYYY)
 * @returns {string} Formatted delivery date
 */
export const formatDeliveryDate = dateStr => {
  const date = parseTaskDate(dateStr)
  if (!date) return dateStr || ''
  const nextDay = new Date(date.getTime() + 24 * 60 * 60 * 1000)
  return formatLongFr(nextDay)
}